/**
 * 📚 LMS Mock Data & Service (v2: with User Persistence)
 * * Adds logic to simulate user progress tracking using localStorage.
 */

// --- 1. Data Structure Interfaces ---

// (Existing Interfaces remain the same: Course, Module, Lesson, Exercise, ContentBlock, LessonResponse)
// ...

// Re-defining existing necessary interfaces for completeness
export interface Course {
  id: number;
  title: string;
  slug: string;
  description: string;
}
export interface Module {
  id: number;
  course_id: number;
  title: string;
  order_index: number;
}
export interface Lesson {
  id: number;
  module_id: number;
  title: string;
  order_index: number;
}
export interface Exercise {
  id: number;
  course_id: number;
  type: "multiple_choice" | "open_text";
  prompt: string;
  data: any;
}
export interface ContentBlockBase {
  id: number;
  lesson_id: number;
  type: "markdown" | "video" | "exercise_ref";
  order_index: number;
}
export interface ExerciseRefBlock extends ContentBlockBase {
  type: "exercise_ref";
  content: null;
  resource_id: number;
  exercise_data: Exercise;
}
export type ContentBlock = ContentBlockBase | ExerciseRefBlock;
export interface LessonResponse {
  lesson_title: string;
  blocks: ContentBlock[];
}

// --- NEW Interfaces for User Progress ---

/**
 * Maps to the `exercise_submissions` table.
 */
export interface ExerciseSubmission {
  id: number; // For simplicity, we can use a timestamp or a sequential counter
  exercise_id: number; // FK
  answer_data: string | number | any; // Student's input/selection
  is_correct: boolean;
  created_at: number; // Timestamp
}

export interface LessonProgress {
  completed: boolean;
  completed_at: number;
}

/**
 * Maps to the `enrollments` table structure in the progress store.
 * We use an object map for efficient lookup by Course ID.
 */
export interface EnrollmentProgress {
  course_id: number;
  status: "active" | "completed";
  last_visited_lesson_id: number | null;
  submissions: Record<number, ExerciseSubmission[]>; // Key: Exercise ID
  lessonsProgress: Record<number, LessonProgress>; // Key: Lesson ID
}

/**
 * The top-level structure stored in localStorage.
 * Key: User ID
 */
export interface UserProgressStore {
  [userId: string]: Record<number, EnrollmentProgress>; // Key: Course ID
}

// --- 2. In-Memory Mock Database (Unchanged) ---

// (Using the same MOCK_COURSES, MOCK_MODULES, MOCK_LESSONS, MOCK_EXERCISES, MOCK_CONTENT_BLOCKS)

const MOCK_COURSES: Course[] = [
  {
    id: 1,
    title: "German Nursing B1: Introduction to Anatomy",
    slug: "german-nursing-b1-anatomy",
    description:
      "A foundational course covering essential anatomical structures and terminology in the German language."
  }
];

const MOCK_MODULES: Module[] = [
  { id: 101, course_id: 1, title: "Cellular & Tissue Basics", order_index: 0 },
  { id: 102, course_id: 1, title: "The Cardiovascular System", order_index: 1 },
  { id: 103, course_id: 1, title: "The Respiratory System", order_index: 2 }
];

const MOCK_LESSONS: Lesson[] = [
  {
    id: 201,
    module_id: 101,
    title: "Introduction to Tissues (Gewebe)",
    order_index: 0
  },
  { id: 202, module_id: 102, title: "The Heart (Das Herz)", order_index: 0 },
  {
    id: 203,
    module_id: 102,
    title: "Blood Vessels (Blutgefäße)",
    order_index: 1
  },
  { id: 204, module_id: 103, title: "The Lungs (Die Lunge)", order_index: 0 }
];

const MOCK_EXERCISES: Exercise[] = [
  {
    id: 500,
    course_id: 1,
    type: "multiple_choice",
    prompt:
      "Which vessel carries deoxygenated blood from the body to the right atrium?",
    data: {
      options: [
        { id: 1, text: "Aorta", is_correct: false },
        { id: 2, text: "Pulmonary Artery", is_correct: false },
        { id: 3, text: "Vena Cava", is_correct: true },
        { id: 4, text: "Pulmonary Vein", is_correct: false }
      ],
      randomize: true
    }
  },
  {
    id: 501,
    course_id: 1,
    type: "open_text",
    prompt: 'What is the German term for "blood pressure"?',
    data: {
      correct_answer: "Blutdruck"
    }
  }
];

const MOCK_CONTENT_BLOCKS: ContentBlock[] = [
  // Lesson 202: The Heart (Das Herz)
  {
    id: 1001,
    lesson_id: 202,
    type: "markdown",
    order_index: 0,
    content: `## Das Herz (The Heart)
Das Herz ist ein hohles muskuläres Organ, das Blut durch die Blutgefäße des Kreislaufsystems pumpt.`
  } as ContentBlock & { type: "markdown" },
  {
    id: 1004,
    lesson_id: 202,
    type: "exercise_ref",
    order_index: 3,
    content: null,
    resource_id: 500, // Which vessel...
    exercise_data: {} as Exercise
  } as ExerciseRefBlock,
  {
    id: 1005,
    lesson_id: 202,
    type: "exercise_ref",
    order_index: 4,
    content: null,
    resource_id: 501, // Blood pressure term
    exercise_data: {} as Exercise
  } as ExerciseRefBlock,
  // Lesson 201: Introduction to Tissues
  {
    id: 1101,
    lesson_id: 201,
    type: "markdown",
    order_index: 0,
    content:
      "## Gewebe (Tissues)\nMan unterscheidet vier Hauptgewebearten: Epithelgewebe, Binde- und Stützgewebe, Muskelgewebe und Nervengewebe."
  } as ContentBlock & { type: "markdown" }
];

// --- 3. localStorage Utility Functions ---

const STORAGE_KEY = "lms_user_progress";

/**
 * Reads the full user progress map from localStorage.
 */
function getProgressStore(): UserProgressStore {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return {};
  }
  const storedData = localStorage.getItem(STORAGE_KEY);
  try {
    return storedData ? JSON.parse(storedData) : {};
  } catch (e) {
    console.error("Failed to parse user progress from localStorage:", e);
    return {};
  }
}

/**
 * Writes the full user progress map back to localStorage.
 */
function saveProgressStore(store: UserProgressStore): void {
  if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  }
}

/**
 * Retrieves the enrollment progress for a specific user and course.
 */
function getEnrollment(
  userId: string,
  courseId: number
): EnrollmentProgress | undefined {
  const store = getProgressStore();
  return store[userId]?.[courseId];
}

/**
 * Creates or updates the enrollment progress for a specific user and course.
 */
function updateEnrollment(
  userId: string,
  courseId: number,
  updateFn: (e: EnrollmentProgress) => EnrollmentProgress
): EnrollmentProgress {
  const store = getProgressStore();

  // Ensure user entry exists
  if (!store[userId]) {
    store[userId] = {};
  }

  // Get current enrollment or create a new one (simulating auto-enrollment on first interaction)
  const currentEnrollment: EnrollmentProgress = store[userId][courseId] || {
    course_id: courseId,
    status: "active",
    last_visited_lesson_id: null,
    submissions: {},
    lessonsProgress: {}
  };

  // Apply the update function
  const newEnrollment = updateFn(currentEnrollment);

  // Save back to the store
  store[userId][courseId] = newEnrollment;
  saveProgressStore(store);

  return newEnrollment;
}

// --- 4. Mock Service Layer ---

export class LmsApiService {
  private async delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // --- Public API Methods ---

  /**
   * Retrieves a list of all courses.
   */
  public async getCourses(): Promise<Course[]> {
    await this.delay(300);
    return MOCK_COURSES;
  }

  /**
   * Retrieves the enrollment status for a user/course.
   */
  public async getEnrollmentStatus(
    userId: string,
    courseId: number
  ): Promise<EnrollmentProgress | undefined> {
    await this.delay(100);
    return getEnrollment(userId, courseId);
  }

  /**
   * Retrieves all modules for a specific course, optionally including the last visited lesson.
   */
  public async getModules(
    userId: string,
    courseId: number
  ): Promise<{ modules: Module[]; enrollment?: EnrollmentProgress }> {
    await this.delay(300);
    const enrollment = getEnrollment(userId, courseId);

    const modules = MOCK_MODULES.filter(
      (module) => module.course_id === courseId
    ).sort((a, b) => a.order_index - b.order_index);

    return { modules, enrollment };
  }

  /**
   * Retrieves all lessons belonging to a specific module.
   */
  public async getLessons(moduleId: number): Promise<Lesson[]> {
    await this.delay(300);
    return MOCK_LESSONS.filter((lesson) => lesson.module_id === moduleId).sort(
      (a, b) => a.order_index - b.order_index
    );
  }

  /**
   * Retrieves the full lesson content and updates the user's progress.
   */
  public async getLessonContent(
    userId: string,
    lessonId: number
  ): Promise<LessonResponse | null> {
    await this.delay(500);

    const lesson = MOCK_LESSONS.find((l) => l.id === lessonId);
    if (!lesson) return null;

    const courseId = MOCK_MODULES.find(
      (m) => m.id === lesson.module_id
    )?.course_id;
    if (!courseId) return null;

    // **PROGRESS UPDATE:** Mark this lesson as last visited
    updateEnrollment(userId, courseId, (e) => ({
      ...e,
      last_visited_lesson_id: lessonId
    }));

    // 1. Get and sort blocks
    const rawBlocks = MOCK_CONTENT_BLOCKS.filter(
      (block) => block.lesson_id === lessonId
    ).sort((a, b) => a.order_index - b.order_index);

    // 2. Expand exercise_ref blocks
    const expandedBlocks: ContentBlock[] = rawBlocks.map((block) => {
      if (block.type === "exercise_ref") {
        const refBlock = block as ExerciseRefBlock;
        const exercise = MOCK_EXERCISES.find(
          (ex) => ex.id === refBlock.resource_id
        );
        if (exercise) {
          refBlock.exercise_data = JSON.parse(JSON.stringify(exercise));
        }
        return refBlock;
      }
      return block;
    });

    return {
      lesson_title: lesson.title,
      blocks: expandedBlocks
    };
  }

  /**
   * Submits an exercise answer, checks correctness, and saves the submission.
   */
  public async submitExercise(
    userId: string,
    courseId: number,
    exerciseId: number,
    answer: string | number
  ): Promise<ExerciseSubmission> {
    await this.delay(400);

    const exercise = MOCK_EXERCISES.find((ex) => ex.id === exerciseId);
    if (!exercise) throw new Error("Exercise not found.");

    let isCorrect: boolean;

    // Simple grading logic based on exercise type
    if (exercise.type === "multiple_choice") {
      const correctOption = exercise.data.options.find(
        (opt: any) => opt.is_correct
      );
      isCorrect = correctOption ? correctOption.id === answer : false;
    } else if (exercise.type === "open_text") {
      // Case-insensitive check
      isCorrect =
        (exercise.data.correct_answer as string).toLowerCase() ===
        String(answer).toLowerCase();
    } else {
      isCorrect = false;
    }

    const submission: ExerciseSubmission = {
      id: Date.now(), // Unique ID generation
      exercise_id: exerciseId,
      answer_data: answer,
      is_correct: isCorrect,
      created_at: Date.now()
    };

    // **PROGRESS UPDATE:** Save the submission
    updateEnrollment(userId, courseId, (e) => {
      if (!e.submissions[exerciseId]) {
        e.submissions[exerciseId] = [];
      }
      e.submissions[exerciseId].push(submission);
      return e;
    });

    return submission;
  }

  /**
   * Marks a lesson as completed.
   */
  public async completeLesson(
    userId: string,
    courseId: number,
    lessonId: number
  ): Promise<void> {
    await this.delay(200);
    updateEnrollment(userId, courseId, (e) => {
      const updatedLessonsProgress = { ...e.lessonsProgress };
      updatedLessonsProgress[lessonId] = {
        completed: true,
        completed_at: Date.now()
      };
      return {
        ...e,
        lessonsProgress: updatedLessonsProgress
      };
    });
  }
}
